// pages/Agents/AgentSpace/AgentSpaceFAB.js
// Global Agent Space chat bubble. Rendered once by ChatbotLayout so it appears
// on every chatbot panel page. Reads state from AgentSpaceContext so it shares
// the picked agent with the AgentSpace page.
//
// When open, two independent floating elements are shown (Asha-style):
//   1. Right-side info DRAWER (~55vw, full height): agent summary + activity +
//      scheduled tasks, live-refreshed. Only when an agent is selected.
//   2. Bottom-left floating CHATBOX card: hosts the agents list (until an
//      agent is picked) and the chat thereafter.
// No backdrop â€” the page behind stays visible and interactive.
import React, { useState, useEffect } from 'react';
import {
    Box,
    Fab,
    IconButton,
    Typography,
    Slide,
    Tooltip,
} from '@mui/material';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PollIcon from '@mui/icons-material/Poll';
import PeopleIcon from '@mui/icons-material/People';
import ChatIcon from '@mui/icons-material/Chat';
import EventIcon from '@mui/icons-material/Event';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import EmailIcon from '@mui/icons-material/Email';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import AgentSpaceDashboard from './AgentSpaceDashboard';
import AgentSpaceChat from './AgentSpaceChat';
import AgentSpaceConsole from './AgentSpaceConsole';
import AgentSpaceAgentInfoPanel from './AgentSpaceAgentInfoPanel';
import { useAgentSpace } from '../../../../contexts/AgentSpaceContext';
import axiosInstance from '../../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textTertiary: '#94a3b8',
    accent: '#8b5cf6',
};

// Map backend icon_name â†’ MUI icon component (matches the agents list / info panel)
const ICON_MAP = {
    PollIcon: (s) => <PollIcon sx={{ fontSize: s }} />,
    PeopleIcon: (s) => <PeopleIcon sx={{ fontSize: s }} />,
    ChatIcon: (s) => <ChatIcon sx={{ fontSize: s }} />,
    EventIcon: (s) => <EventIcon sx={{ fontSize: s }} />,
    MailOutlineIcon: (s) => <MailOutlineIcon sx={{ fontSize: s }} />,
    EmailIcon: (s) => <EmailIcon sx={{ fontSize: s }} />,
    CalendarMonthIcon: (s) => <CalendarMonthIcon sx={{ fontSize: s }} />,
    CleaningServicesIcon: (s) => <CleaningServicesIcon sx={{ fontSize: s }} />,
};

function getAgentIcon(iconName, size = 16) {
    const fn = ICON_MAP[iconName];
    return fn ? fn(size) : <AutoAwesomeIcon sx={{ fontSize: size }} />;
}

export default function AgentSpaceFAB({ chatbotId }) {
    const {
        selectedAgent,
        selectAgent,
        configureAgent,
        fabOpen,
        setFabOpen,
        fabVisible,
        workflowsRefreshKey,
        bumpWorkflows,
    } = useAgentSpace();

    // What the chatbox card is showing: 'agents' | 'chat' | 'console'
    const [view, setView] = useState(selectedAgent ? 'chat' : 'agents');
    // Set to `true` while a chat message is in flight so the info panel can
    // poll the activity feed and surface steps as the engine writes them
    // (task_summary → tool_call(running) → tool_call(completed) → final).
    const [liveProcessing, setLiveProcessing] = useState(false);
    // Split-screen mode: chat takes 2/3 of the screen on the left, info drawer
    // shrinks to 1/3 on the right. Toggled by the maximize button in the
    // chatbox header. Reverts to the default floating-card layout when off.
    const [expanded, setExpanded] = useState(false);

    // Agent-type list so we can resolve the selectedAgent ID into a display
    // label for the chatbox header ("Chat with CRM", "Chat with Survey", ...).
    const [agentTypes, setAgentTypes] = useState([]);
    useEffect(() => {
        if (!chatbotId) return;
        let cancelled = false;
        axiosInstance.get('/api/agent-space/agent-types')
            .then((res) => { if (!cancelled && res.data?.success) setAgentTypes(res.data.data || []); })
            .catch(() => { /* noop */ });
        return () => { cancelled = true; };
    }, [chatbotId]);
    const selectedAgentDef = selectedAgent
        ? agentTypes.find((a) => a.id === selectedAgent)
        : null;
    const selectedAgentLabel = selectedAgentDef?.label || selectedAgent || null;
    const selectedAgentColor = selectedAgentDef?.color || colors.accent;

    const handleOpen = () => {
        setView(selectedAgent ? 'chat' : 'agents');
        setFabOpen(true);
    };

    const handleClose = () => setFabOpen(false);

    useEffect(() => {
        if (fabOpen) setView(selectedAgent ? 'chat' : 'agents');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fabOpen]);

    const handlePickAgent = (agentId) => {
        // selectAgent both sets the agent in context and re-opens the FAB
        // if needed (idempotent when already open).
        selectAgent(agentId);
        setView('chat');
    };

    const handleBackToAgents = () => {
        setView('agents');
    };

    // Show the right info drawer only when the chatbox is actually on the
    // chat view for the selected agent. Going Back to the agents list (or
    // switching to console) hides the info drawer immediately, without
    // having to clear the selected agent in the parent.
    const showInfoDrawer = !!selectedAgent && view === 'chat';
    // The expand button only has meaning when both panels are visible. Out of
    // chat / agents-list / console views we ignore the toggle.
    const isExpanded = expanded && showInfoDrawer;

    // Don't render anything if we don't have a chatbotId yet (e.g. before
    // ChatbotLayout has resolved the active chatbot).
    if (!chatbotId) return null;
    // User has toggled the bubble off via the show/hide control on the
    // Agent Space landing page. Skip rendering the bubble and any
    // attached chat/drawer surfaces entirely.
    if (!fabVisible) return null;

    return (
        <>
            {/* Floating agent bubble (robot/agent icon) */}
            {!fabOpen && (
                <Tooltip title="Open agents">
                    <Fab
                        onClick={handleOpen}
                        sx={{
                            position: 'fixed',
                            bottom: { xs: 16, sm: 24 },
                            right: { xs: 16, sm: 24 },
                            zIndex: 1300,
                            background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                            color: '#fff',
                            boxShadow: '0 10px 28px rgba(139,92,246,0.45)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #6d28d9, #8b5cf6)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 14px 32px rgba(139,92,246,0.55)',
                            },
                            transition: 'all 0.2s ease',
                        }}
                    >
                        <SmartToyOutlinedIcon sx={{ fontSize: 26 }} />
                    </Fab>
                </Tooltip>
            )}

            {/* ============================================================
                RIGHT-SIDE INFO DRAWER
                ============================================================ */}
            <Slide direction="left" in={fabOpen && showInfoDrawer} mountOnEnter unmountOnExit>
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        right: 0,
                        height: '100vh',
                        // Default: 55vw. Expanded: shrink to 1/3 so the chat
                        // can take the remaining 2/3 on the left.
                        width: isExpanded
                            ? { xs: '100vw', md: '33.333vw' }
                            : { xs: '100vw', md: '55vw' },
                        bgcolor: 'rgba(255,255,255,0.98)',
                        backdropFilter: 'blur(16px)',
                        borderLeft: '1px solid rgba(0,0,0,0.08)',
                        boxShadow: '-24px 0 64px rgba(0,0,0,0.18)',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        zIndex: 1300,
                        fontFamily,
                        transition: 'width 0.3s ease',
                    }}
                >
                    <AgentSpaceAgentInfoPanel
                        chatbotId={chatbotId}
                        agentType={selectedAgent}
                        refreshKey={workflowsRefreshKey}
                        liveProcessing={liveProcessing}
                        onClose={handleClose}
                    />
                </Box>
            </Slide>

            {/* ============================================================
                FLOATING CHATBOX CARD
                Position depends on whether the info drawer is open:
                  - No agent selected â†’ bottom-right (where the FAB was),
                    so the panel opens upward from the bubble's spot.
                  - Agent selected â†’ docked immediately left of the info
                    drawer with a 1px gap.
                ============================================================ */}
            <Slide direction="up" in={fabOpen} mountOnEnter unmountOnExit>
                <Box
                    sx={{
                        position: 'fixed',
                        // Expanded mode: chat takes 2/3 of the screen on the
                        // left, full height, edge-to-edge. Default mode: floats
                        // as a small card next to / above the info drawer.
                        ...(isExpanded
                            ? {
                                top: 0,
                                left: 0,
                                bottom: 'auto',
                                // 10px short of 2/3 so there's a visible gap
                                // between the chatbox and the info panel (which
                                // sits flush at right:0 with width 33.333vw).
                                right: 'auto',
                                width: { xs: '100vw', md: 'calc(66.667vw - 10px)' },
                                height: '100vh',
                                maxHeight: '100vh',
                                borderRadius: 0,
                                border: 'none',
                                borderRight: 'none',
                                boxShadow: 'none',
                            }
                            : {
                                bottom: { xs: 16, sm: 24 },
                                right: showInfoDrawer
                                    ? { xs: 16, md: 'calc(55vw + 1px)' }
                                    : { xs: 16, sm: 24 },
                                left: 'auto',
                                top: 'auto',
                                width: { xs: 'calc(100vw - 32px)', sm: 400 },
                                height: { xs: 'calc(100vh - 80px)', sm: 540 },
                                maxHeight: '85vh',
                                borderRadius: '18px',
                                border: '1px solid rgba(0,0,0,0.08)',
                                boxShadow: '0 24px 64px rgba(0,0,0,0.22)',
                            }),
                        bgcolor: 'rgba(255,255,255,0.98)',
                        backdropFilter: 'blur(16px)',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        zIndex: 1301,
                        fontFamily,
                        transition: 'width 0.3s ease, height 0.3s ease, right 0.3s ease, left 0.3s ease, border-radius 0.3s ease',
                    }}
                >
                    <Box sx={{
                        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                        px: { xs: 1.5, sm: 2 }, py: { xs: 1.25, sm: 1.5 },
                        borderBottom: '1px solid rgba(0,0,0,0.06)',
                        background: 'linear-gradient(135deg, rgba(139,92,246,0.06), rgba(109,40,217,0.04))',
                        flexShrink: 0,
                        gap: 1,
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, minWidth: 0, flex: 1 }}>
                            {(view === 'chat' || view === 'console') && (
                                <IconButton size="small" onClick={handleBackToAgents} sx={{ mt: '-2px' }}>
                                    <ArrowBackIcon sx={{ fontSize: 18, color: colors.textSecondary }} />
                                </IconButton>
                            )}
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                    {/* Agent icon (only in chat view, when we have the def) */}
                                    {view === 'chat' && selectedAgentDef && (
                                        <Box sx={{
                                            width: 22, height: 22, borderRadius: '7px',
                                            bgcolor: `${selectedAgentColor}18`,
                                            color: selectedAgentColor,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            flexShrink: 0,
                                        }}>
                                            {getAgentIcon(selectedAgentDef.icon_name, 14)}
                                        </Box>
                                    )}
                                    <Typography sx={{
                                        fontFamily, fontSize: '0.9rem', fontWeight: 700, color: colors.textPrimary,
                                    }} noWrap>
                                        {view === 'console'
                                            ? 'Console'
                                            : view === 'chat'
                                                ? `Chat with ${selectedAgentLabel || 'agent'}`
                                                : 'Agents'}
                                    </Typography>
                                </Box>
                                {view === 'agents' && (
                                    <Typography sx={{ fontFamily, fontSize: '0.7rem', color: colors.textTertiary, mt: 0.25, lineHeight: 1.4 }}>
                                        Select an agent to view its activity, schedule tasks, or start a conversation.
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                            {/* Show the expand/collapse toggle only when the
                                chat-with-info-drawer is on screen. The toggle
                                has no meaning on the agents list or console
                                views (no drawer to expand against). */}
                            {showInfoDrawer && (
                                <Tooltip title={isExpanded ? 'Collapse' : 'Expand chat'}>
                                    <IconButton size="small" onClick={() => setExpanded((v) => !v)}>
                                        {isExpanded
                                            ? <FullscreenExitIcon sx={{ fontSize: 18, color: colors.textSecondary }} />
                                            : <FullscreenIcon sx={{ fontSize: 18, color: colors.textSecondary }} />}
                                    </IconButton>
                                </Tooltip>
                            )}
                            <IconButton size="small" onClick={handleClose}>
                                <CloseIcon sx={{ fontSize: 18, color: colors.textSecondary }} />
                            </IconButton>
                        </Box>
                    </Box>

                    <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', minHeight: 0 }}>
                        {view === 'agents' && (
                            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                                <AgentSpaceDashboard
                                    chatbotId={chatbotId}
                                    onSelectAgent={handlePickAgent}
                                    onConfigureAgent={configureAgent}
                                    showHeader={false}
                                />
                            </Box>
                        )}
                        {view === 'chat' && (
                            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                                <AgentSpaceChat
                                    sessionCode={null}
                                    chatbotId={chatbotId}
                                    initialAgentType={selectedAgent}
                                    onScheduledTaskCreated={bumpWorkflows}
                                    onSessionChange={(code) => { if (code) bumpWorkflows(); }}
                                    onMessageStarted={() => setLiveProcessing(true)}
                                    onMessageComplete={() => {
                                        setLiveProcessing(false);
                                        bumpWorkflows();
                                    }}
                                />
                            </Box>
                        )}
                        {view === 'console' && (
                            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                                <AgentSpaceConsole chatbotId={chatbotId} />
                            </Box>
                        )}
                    </Box>
                </Box>
            </Slide>
        </>
    );
}
